import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CRACreditService } from "../../../services/CRACreditService";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { CountryService } from "../../../services/common/CountryService";
import { DateRange } from "@mui/icons-material";
import { DepartmentsService } from "../../../services/DepartmentsService";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { StatutoryHolidayReportDataGridWithColumnToggled } from "./StatutoryHolidayReportDataGridWithColumnToggled";
import { StatutoryHolidayReportHeader } from "./StatutoryHolidayReportHeader";
import { StatutoryHolidayService } from "../../../services/StatutoryHolidayService";
import { TeamsService } from "../../../services/TeamsService";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IStatutoryHolidayReportSetupPage { }
const STATUTORY_HOLIDAY_REPORT_QUERY_KEY = "statutory-holiday-report";

const useStatutoryHolidayReport = (
	runGetListAPICall: boolean,
	clientId: string,
	provinceId: string,
	holidayDate: string,
	employeeId: string,
	payPeriodEndDate: string,
	payPeriod: string,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			STATUTORY_HOLIDAY_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			provinceId,
			holidayDate,
			employeeId,
			payPeriodEndDate,
			payPeriod,
			startDate,
			endDate,
			params,
		],
		queryFn: () =>
			ReportsService.getStatutoryHolidayReport(
				runGetListAPICall,
				clientId,
				provinceId,
				holidayDate,
				employeeId,
				payPeriodEndDate,
				payPeriod,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});


const useGetPayPeriodEndDateDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
const useStatutoryHolidayDDL = () =>
	useQuery({
		queryKey: ["statutory-holiday-ddl"],
		queryFn: (data) => {
			return StatutoryHolidayService.getStatutoryHolidayDDL();
		},
		refetchOnWindowFocus: false,
	});


export const StatutoryHolidayReport: React.FC<IStatutoryHolidayReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const countryList = useSelector((state: IAppState) => state.countryAndProvincesInfo.countryDDL);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [statutoryHolidayReportData, setStatutoryHolidayReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);
	const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState<string | null>(null);
	const [provinceDDL, setProvinceDDL] =
		useState<any>(undefined);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "employeeName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	useEffect(() => {
		if (countryList && Array.isArray(countryList)) {
			const canada = countryList.find(
				(item: any) => item.label === "Canada" || "canada"
			);
			if (canada) {
				(async () => {
					try {
						const response = await CountryService.getProvinceOrStateDDL(
							navigateToLogout,
							canada.value,
							"Province"
						);
						setProvinceDDL(response);
					} catch (err) {
						console.error("error", err);
					}
				})();
			} else {
				setProvinceDDL(undefined);
			}
		}
	}, [countryList]);

	console.log("countryListttt", countryList)
	console.log("provincesDDL", provinceDDL)

	const provinceId = formMethods.getValues("provinceId");
	const holidayDate = formMethods.getValues("holidayDate");
	const employeeId = formMethods.getValues("employeeId");
	const payPeriodEndDate = formMethods.getValues("payPeriodEndDate");
	const payPeriod = formMethods.getValues("payPeriod");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useStatutoryHolidayReport(
		runGetListAPICall,
		clientIdStore,
		provinceId,
		holidayDate,
		employeeId,
		payPeriodEndDate,
		payPeriod,
		formatDate(startDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const statutoryHolidayReportList: any | null = data;
	console.log("listStatutoryHolidayData", statutoryHolidayReportList)
	useEffect(() => {
		if (statutoryHolidayReportList) {
			setStatutoryHolidayReportData(statutoryHolidayReportList);
		}
	}, [statutoryHolidayReportList]);

	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isLoading: isLoadingPayrPeriodFrequencyDDL, data: payPeriodFrequnecyDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	const { isLoading: isLoadingStatutoryHolidayDDL, data: statutoryHolidayDDL } = useStatutoryHolidayDDL();
	const { isLoading: isLoadingPayPeriodEndDateDDL, data: payPeriodEndDateDDL } = useGetPayPeriodEndDateDDL(clientId, selectedPeriodEndDate, taxYear);

	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const provinceDDLItems = useMemo(() => {
		if (provinceDDL) {
			if (
				provinceDDL &&
				Array.isArray(provinceDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(provinceDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [provinceDDL]);

	const statutoryHolidayDDLItems = useMemo(() => {
		if (statutoryHolidayDDL) {
			if (
				statutoryHolidayDDL &&
				Array.isArray(statutoryHolidayDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(statutoryHolidayDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "-- Select Holiday --",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [statutoryHolidayDDL]);

	const payPeriodEndDateDDLItems = useMemo(() => {
		if (payPeriodEndDateDDL) {
			if (
				payPeriodEndDateDDL &&
				Array.isArray(payPeriodEndDateDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodEndDateDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [{
				label: "All",
				value: "all",
			}];
		}
		return [{
			label: "All",
			value: "all",
		}];
	}, [payPeriodEndDateDDL]);


	const payPeriodFrequencyDDLItems = useMemo(() => {
		if (payPeriodFrequnecyDDL) {
			if (
				payPeriodFrequnecyDDL &&
				Array.isArray(payPeriodFrequnecyDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodFrequnecyDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodFrequnecyDDL]);

	//   after click apply button api calls with updated values and update statutory Holiday report data
	const StatutoryHolidayReportDataAPICall = (
		provinceId: string,
		holidayDate: string,
		employeeId: string,
		payPeriodEndDate: string,
		payPeriod: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getStatutoryHolidayReport(
					true,
					clientIdStore,
					provinceId,
					holidayDate,
					employeeId,
					payPeriodEndDate,
					payPeriod,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setStatutoryHolidayReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {

		StatutoryHolidayReportDataAPICall(
			values.provinceId,
			values.holidayDate,
			values.employeeId,
			values.payPeriodEndDate,
			values.payPeriod,
			values.startDate,
			values.endDate,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "employeeName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getStatutoryHolidayAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [STATUTORY_HOLIDAY_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["statutory-holiday-report"]);

	// const updatedStatutoryHolidayReportListItems = useMemo(() => {
	// 	return (
	// 		statutoryHolidayReportData &&
	// 		statutoryHolidayReportData.items &&
	// 		statutoryHolidayReportData?.items.map((item: any) => ({
	// 			...item,
	// 		}))
	// 	);
	// }, [statutoryHolidayReportData]);

	const updatedStatutoryHolidayReportListItems = useMemo(() => {
		if (
			!statutoryHolidayReportData ||
			!statutoryHolidayReportData.items
		) {
			return statutoryHolidayReportData?.items;
		}

		return [...statutoryHolidayReportData.items]
			.map((item: any) => ({
				...item,
				employeeGroupKey: `${item.employeeId}__${item.payPeriodEndDate}`,
			}))
			.sort((a: any, b: any) => {
				// 1. sort the whole list chronologically by payPeriodEndDate (Jan group before Feb group)
				const dateA = new Date(a.payPeriodEndDate).getTime();
				const dateB = new Date(b.payPeriodEndDate).getTime();
				if (dateA !== dateB) return dateA - dateB;

				// 2. within the same payPeriodEndDate, order employee groups alphabetically by name
				const nameCompare = (a.employeeName || "").localeCompare(b.employeeName || "");
				if (nameCompare !== 0) return nameCompare;

				// 3. within the same employee + pay period, keep original row order (stable sort)
				return 0;
			});
	}, [statutoryHolidayReportData]);

	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"provinceId",
		"holidayDate",
		"employeeId",
		"payPeriodEndDate",
		"payPeriod",
		"startDate",
		"endDate",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [provinceId, employeeId, payPeriod, holidayDate, payPeriodEndDate, startDate, endDate] = values;

		try {
			setIsLoading(true);
			const response = await ReportsService.exportStatutoryHolidayReport(
				selectedOption,
				clientIdStore,
				provinceId,
				holidayDate,
				employeeId,
				payPeriodEndDate,
				payPeriod,
				formatDate(startDate),
				formatDate(endDate),
				queryParams
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName}-holiday.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName}-holiday.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName}-holiday.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};

	const expandedLabels = ["Reports"];

	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];


			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");


			const isStatutoryHolidayUnderPayroll =
				item.label === "Statutory Holiday" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) || // e.g. "Reports"
				isPayrollUnderReports ||
				isStatutoryHolidayUnderPayroll;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: isStatutoryHolidayUnderPayroll
					? "/reports/statutory-holiday"
					: item.path ?? undefined,
			};
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>

			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Statutory Holiday"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<StatutoryHolidayReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						employeeDDLItems={employeeDDLItems}
						payPeriodFrequencyDDLItems={payPeriodFrequencyDDLItems}
						payPeriodEndDateDDL={payPeriodEndDateDDLItems ?? []}
						provinceDDL={provinceDDLItems}
						statutoryHolidayDDL={statutoryHolidayDDLItems}
						onPeriodEndDateSelectionChangeHandler={(value: string) => setSelectedPeriodEndDate(value)}
						formMethods={formMethods}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading || isLoadingPayrPeriodFrequencyDDL || isLoadingemployeeDDL || isLoadingPayPeriodEndDateDDL || isLoadingStatutoryHolidayDDL
					}
					testId="statutory-holiday-report"
				/>

				<StatutoryHolidayReportDataGridWithColumnToggled
					columns={
						statutoryHolidayReportData?.headerColumns
							? statutoryHolidayReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// statutoryHolidayReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						statutoryHolidayReportData
							&& statutoryHolidayReportData
								?.totalHeaderColumns &&
							Object.keys(statutoryHolidayReportData.totalHeaderColumns).length !== 0
							? statutoryHolidayReportData.totalHeaderColumns
							: {}
						// statutoryHolidayReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						statutoryHolidayReportData && updatedStatutoryHolidayReportListItems
							? updatedStatutoryHolidayReportListItems
							: []
						// statutoryHolidayReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						statutoryHolidayReportData && statutoryHolidayReportData.total
							? statutoryHolidayReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Statutory Holiday"}
					onExportButtonClick={onExportButtonClick}
				/>

				{statutoryHolidayReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
